import { useState, useEffect } from 'react';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import appService from '../../service/app.service';

const createOptionKey = (option, index = 0) => {
  return [option?.value ?? '', option?.label ?? '', option?.type ?? '', index].join('__');
};

const normalizeOptions = (options = []) => {
  return options.map((option, index) => ({
    ...option,
    _optionKey: option?._optionKey ?? createOptionKey(option, index),
  }));
};

const AutoCompleteSelectUI = (props) => {
  const {
    id,
    label,
    placeholder,
    table,
    listType,
    selectedValue,
    selectedLabelValue,
    conditions,
    joins,
    multiple,
    selectedColumns,
    dataset,
    options,
    value: propValue,
    variant,
    isOptionEqualToValue,
    helperText,
  } = props;

  const [list, setList] = useState([]);
  const [defaultList, setDefaultList] = useState([]);
  const [value, setValue] = useState(multiple ? [] : null);

  useEffect(() => {
    fetchData();
  }, [table, conditions]);

  useEffect(() => {
    setList(defaultList);
  }, [defaultList]);

  useEffect(() => {
    if (dataset) setDefaultList(normalizeOptions(dataset));
  }, [dataset]);

  useEffect(() => {
    if (options) setDefaultList(normalizeOptions(options));
  }, [options]);

  useEffect(() => {
    const effectiveSelectedValue = selectedValue ?? propValue;

    if (
      !multiple &&
      effectiveSelectedValue &&
      typeof effectiveSelectedValue === 'object' &&
      !Array.isArray(effectiveSelectedValue)
    ) {
      const normalizedValue = normalizeOptions([effectiveSelectedValue])[0];
      const findedListItem = defaultList.find((option) =>
        (isOptionEqualToValue ?? ((optionItem, valueItem) => optionItem?.value === valueItem?.value))(
          option,
          normalizedValue
        )
      );

      setList(findedListItem ? defaultList : [...defaultList, normalizedValue]);
      setValue(findedListItem || normalizedValue);
      return;
    }

    const findedListItem = defaultList.find((p) => p.value === effectiveSelectedValue);

    if (
      !findedListItem &&
      ((typeof effectiveSelectedValue === 'number' && effectiveSelectedValue > 0) ||
        (typeof effectiveSelectedValue === 'string' && effectiveSelectedValue.trim().length > 0))
    ) {
      const newVal = {
        value: effectiveSelectedValue,
        label:
          selectedLabelValue ??
          (!Array.isArray(propValue) && typeof propValue === 'object' ? propValue?.label ?? '' : ''),
        _optionKey: createOptionKey(
          {
            value: effectiveSelectedValue,
            label:
              selectedLabelValue ??
              (!Array.isArray(propValue) && typeof propValue === 'object' ? propValue?.label ?? '' : ''),
          },
          defaultList.length
        ),
      };
      setList([...defaultList, newVal]);
      setValue(newVal);
    } else {
      if (multiple) {
        setList(defaultList);
        setValue(Array.isArray(effectiveSelectedValue) ? effectiveSelectedValue : []);
      } else {
        setList(defaultList);
        setValue(findedListItem || null);
      }
    }
  }, [selectedValue, selectedLabelValue, defaultList, multiple, propValue, isOptionEqualToValue]);

  const fetchData = () => {
    if (table) {
      const requestData = {
        tables: Array.isArray(table) ? table : [table],
        joins: Array.isArray(joins) ? joins : joins ? [joins] : [],
        conditions: Array.isArray(conditions) ? conditions : conditions ? [conditions] : [],
        selectedColumns: Array.isArray(selectedColumns) ? selectedColumns : selectedColumns ? [selectedColumns] : ['*'],
      };

      appService.getJoinedData(requestData).then((data) => {
        const formattedData = data.map((item) => ({
          label: item[listType.name],
          value: item[listType.id],
          ...(listType?.type ? { type: item[listType.type] } : item.type ? { type: item.type } : {}),
          ...(item.icon ? { icon: item.icon } : {}),
        }));
        setDefaultList(normalizeOptions(formattedData));
      });
    } else {
      setDefaultList([]);
    }
  };

  const filterOptions = createFilterOptions({
    limit: 100,
  });

  const handleKeyDown = (event) => {
    if (event.key === 'Tab') {
      const inputValue = event.target?.value?.trim();
      if (inputValue) {
        const exactOption = list.find(
          (opt) => String(opt.label).trim() === inputValue || String(opt.value).trim() === inputValue
        );

        if (exactOption) {
          if (multiple) {
            const current = Array.isArray(value) ? value : [];
            const exists = current.some((opt) => opt.value === exactOption.value);
            const newValue = exists ? current : [...current, exactOption];
            setValue(exactOption);
            if (props.onChange) {
              props.onChange(event, newValue);
            }
          } else {
            setValue(exactOption);
            if (props.onChange) {
              props.onChange(event, exactOption);
            }
          }
        }
      }
    }

    if (props.onKeyDown) {
      props.onKeyDown(event);
    }
  };

  const handleChange = (event, newValue) => {
    setValue(newValue);
    if (props.onChange) {
      props.onChange(event, newValue);
    }
  };

  return (
    <div className="z-50 w-full">
      <Autocomplete
        {...props}
        clearOnEscape
        id={id}
        name={id}
        value={value}
        options={list}
        getOptionLabel={(option) => option?.label ?? ''}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        isOptionEqualToValue={isOptionEqualToValue ?? ((option, val) => option?.value === val?.value)}
        sx={{ width: '100%' }}
        slotProps={{
          popper: {
            placement: 'bottom-start',
            sx: {
              width: 'auto !important',
              maxWidth: '100vw', // do not exceed viewport width
              '& .MuiAutocomplete-paper': {
                width: 'max-content',
                minWidth: 150,
                maxWidth: 'inherit', // respect viewport limit
                whiteSpace: 'normal', // wrap long labels
              },
            },
          },
        }}
        filterOptions={filterOptions}
        renderInput={(params) => {
          const selectedIcon = !multiple && value?.icon ? value.icon : null;
          return (
            <TextField
              {...params}
              variant={variant ?? 'standard'}
              label={label}
              placeholder={placeholder}
              helperText={helperText}
              InputProps={{
                ...params.InputProps,
                startAdornment: selectedIcon ? (
                  <InputAdornment position="start">{selectedIcon}</InputAdornment>
                ) : (
                  params.InputProps.startAdornment
                ),
              }}
            />
          );
        }}
        renderOption={(props, option) => {
          const { key, ...optionProps } = props;
          return (
            <li {...optionProps} key={option?._optionKey ?? key}>
              {option.icon ? <span style={{ display: 'inline-flex', marginRight: 8 }}>{option.icon}</span> : null}
              {option.label}
            </li>
          );
        }}
      />
    </div>
  );
};
export default AutoCompleteSelectUI;
